import { useState, useEffect, useMemo, useCallback } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import useFetchData from '../../../../hooks/UseFetchData'
import { payersOptionsFormat } from '../../../../constants/BeneficiaryConstants'
import { fetchData } from '../../../../helpers/fetchData'
import {
  addPreClaimSubmissionJob,
  updatePreClaimSubmissionJob,
  removePreClaimSubmissionJob,
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getOneMonthMaxDate, getMinDate } from '../../../../utils/dateRangeValidation'
import CustomTable from '../../../../shared/CustomTable'

const ENDPOINT_PREVIEW = '/v3/reports/pre-claim-submission'
const ENDPOINT_JOBS    = '/v3/reports/pre-claim-submission/jobs'

const previewColumns = t => [
  { header: t('Invoice Date'),            body: r => r.InvoiceDate || '—' },
  { header: t('MRN'),                 body: r => r.FileNo || '—' },
  { header: t('Document ID'),             body: r => r.DocumentID || '—' },
  { header: t('Patient Name'),            body: r => r.PatientName || '—' },
  { header: t('Episode No'),              body: r => r.EpisodeNo || '—' },
  { header: t('Invoice Number'),          body: r => r.InvoiceNumber || '—' },
  { header: t('Policy Number'),           body: r => r.PolicyNumber || '—' },
  { header: t('Membership'),              body: r => r.Membership || '—' },
  { header: t('Department'),              body: r => r.Department || '—' },
  { header: t('Physician'),               body: r => r.Physician || '—' },
  { header: t('Item Code'),               body: r => r.SourceItemCode || '—' },
  { header: t('Item Description'),        body: r => r.ItemDescription || '—' },
  { header: t('Agreed Code'),             body: r => r.AgreedCode || '—' },
  { header: t('Agreed Desc'),             body: r => r.AgreedDesc || '—' },
  { header: t('Non Standard Code'),       body: r => r.NonStandardCode || '—' },
  { header: t('Standard Description'),    body: r => r.StandardDesc || '—' },
  { header: t('Non Standard Description'),body: r => r.NonStandardDesc || '—' },
  {
    header: t('Gross'),
    bodyCellClass: 'text-right',
    body: r => Number(r.Gross ?? 0).toFixed(2),
  },
  {
    header: t('Discount'),
    bodyCellClass: 'text-right',
    body: r => Number(r.Discount ?? 0).toFixed(2),
  },
  {
    header: t('Net'),
    bodyCellClass: 'text-right',
    body: r => Number(r.Net ?? 0).toFixed(2),
  },
  {
    header: t('Patient Share'),
    bodyCellClass: 'text-right',
    body: r => Number(r.PatientShare ?? 0).toFixed(2),
  },
  {
    header: t('Payer Net'),
    bodyCellClass: 'text-right',
    body: r => Number(r.PayerNet ?? 0).toFixed(2),
  },
  {
    header: t('Payer VAT'),
    bodyCellClass: 'text-right',
    body: r => Number(r.PayerVAT ?? 0).toFixed(2),
  },
  {
    header: t('Payer Share'),
    bodyCellClass: 'text-right',
    body: r => Number(r.PayerShare ?? 0).toFixed(2),
  },
  {
    header: t('Claim Total'),
    bodyCellClass: 'text-right',
    body: r => Number(r.ClaimTotal ?? 0).toFixed(2),
  },
]

function buildPayload(filters, { format, skipItemCategory } = {}) {
  const payload = {
    start_date: moment(filters.start_date).format('YYYY-MM-DD'),
    end_date:   moment(filters.end_date).format('YYYY-MM-DD'),
  }

  if (filters.payer_id)           payload.payer_id           = filters.payer_id
  if (filters.department_id)      payload.department_id      = filters.department_id
  if (filters.policy_numbers?.length) payload.policy_numbers = filters.policy_numbers
  if (filters.invoice_number)     payload.invoice_number     = filters.invoice_number
  if (filters.file_number)        payload.file_number        = filters.file_number
  if (filters.standard_code)      payload.item_code          = filters.standard_code
  if (filters.agreed_code)        payload.agreed_code        = filters.agreed_code
  if (filters.agreed_desc)        payload.agreed_desc        = filters.agreed_desc
  if (filters.non_standard_code)  payload.non_standard_code  = filters.non_standard_code
  if (filters.standard_description)  payload.standard_description   = filters.standard_description
  if (filters.non_standard_description) payload.non_standard_description = filters.non_standard_description
  if (filters.is_nahdi !== '') payload.is_nahdi = filters.is_nahdi

  if (skipItemCategory) {
    payload.skip_item_category = true
  } else if (filters.category) {
    payload.item_category = filters.category
  }

  if (format) payload.format = format

  return payload
}

const PreClaimSubmissionReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const jobs = useSelector(state => state.app.preClaimSubmissionJobs || [])
  const loggedUser = useSelector(state => state.auth.user)
  const shouldSkipItemCategory = Number(loggedUser?.business_id) === 27

  const { filters, setFilters, resetFilters } = usePersistedFilters(
    'pre_claim_submission_report',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date:   moment().toDate(),
      payer_id:              '',
      department_id:         '',
      policy_numbers:        [],
      invoice_number:        '',
      file_number:           '',
      standard_code:         '',
      agreed_code:           '',
      agreed_desc:           '',
      non_standard_code:     '',
      non_standard_description: '',
      standard_description:  '',
      is_nahdi: '',
      category: 'insurance',
      format:   'excel',
    }
  )

  const [payers,      setPayers]      = useState([])
  const [departments, setDepartments] = useState([])
  const [policies,    setPolicies]    = useState([])

  const { executeFetch: fetchPayers   } = useFetchData()
  const { executeFetch: fetchPolicies } = useFetchData()

  const [previewData,    setPreviewData]    = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)

  const {
    loading: jobLoading,
    downloadingJobs,
    startJob,
    downloadReport,
    removeJob,
    retryJob,
  } = useAsyncReport({
    addJobAction:    addPreClaimSubmissionJob,
    updateJobAction: updatePreClaimSubmissionJob,
    removeJobAction: removePreClaimSubmissionJob,
    createEndpoint:   ENDPOINT_JOBS,
    statusEndpoint:   ENDPOINT_JOBS,
    downloadEndpoint: ENDPOINT_JOBS,
    fileNamePrefix: 'pre_claim_submission_report',
    existingJobs: jobs,
    buildPayload: (filters, format) =>
      buildPayload(filters, { format, skipItemCategory: shouldSkipItemCategory }),
  })

  const loadDepartments = useCallback(async () => {
    try {
      const res = await fetchData('POST', '/v3/department/getList', {})
      if (res?.data) {
        setDepartments(res.data.map(d => ({ label: d.name_en, value: d.id })))
      }
    } catch (e) { console.log(e) }
  }, [])

  const loadPayers = useCallback(async () => {
    const res = await fetchPayers('POST', '/v3/payer/get', {})
    if (res?.data) setPayers(res.data)
  }, [fetchPayers])

  const loadPolicies = useCallback(async () => {
    if (filters.payer_id) {
      const res = await fetchPolicies('POST', '/v3/insurancePolicy/getByPayerId', {
        payer_id: filters.payer_id,
      })
      if (res?.data) {
        setPolicies(res.data.map(p => ({ label: p.policy_number, value: p.policy_number })))
      }
    } else {
      setPolicies([])
      setFilters(prev => ({ ...prev, policy_numbers: [] }))
    }
  }, [filters.payer_id, fetchPolicies])

  useEffect(() => { loadDepartments() }, [loadDepartments])
  useEffect(() => { loadPayers()      }, [loadPayers])
  useEffect(() => { loadPolicies()    }, [loadPolicies])

  const handleChange = async e => {
    const { name, value } = e.target
    if (name === 'payer_id' && value) {
      const selectedPayer = payers.find(p => p.payer_id === value)
      setFilters(prev => ({
        ...prev,
        payer_id: value,
        category: selectedPayer?.category || 'insurance',
      }))
      // Fetch policies immediately after payer is selected
      await loadPolicies()
    } else if (name === 'payer_id' && !value) {
      setFilters(prev => ({ ...prev, payer_id: '', category: 'insurance' }))
      await loadPolicies()
    } else {
      setFilters(prev => ({ ...prev, [name]: value }))
    }
  }

  const handlePreview = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    setPreviewLoading(true)
    setPreviewData([])
    const payload = buildPayload(filters, { skipItemCategory: shouldSkipItemCategory })
    const res = await fetchData('POST', ENDPOINT_PREVIEW, payload)
    setPreviewData(res?.data || [])
    setPreviewLoading(false)
  }

  const handleExport = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    await startJob(filters, filters.format)
  }

  const handleReset = () => {
    resetFilters()
    setPreviewData([])
  }

  const filterConfig = useMemo(() => [
    { type: 'date',  name: 'start_date', label: 'Start Date', width: 'w-[160px]' },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(filters.start_date),
      maxDate: getOneMonthMaxDate(filters.start_date)
    },
    {
      type: 'dropdown', name: 'payer_id', label: 'Payer',
      options: payersOptionsFormat(payers),
      placeholder: 'All Payers', filter: true, width: 'w-[190px]',
    },
    {
      type: 'dropdown', name: 'policy_numbers', label: 'Policy Numbers',
      options: policies,
      placeholder: 'All Policies', filter: true, multiple: true,
      disabled: !filters.payer_id, width: 'w-[190px]',
    },
    {
      type: 'dropdown', name: 'department_id', label: 'Department',
      options: departments,
      placeholder: 'All Departments', filter: true, width: 'w-[190px]',
    },
    { type: 'text', name: 'invoice_number',  label: 'Invoice Number',  placeholder: 'Invoice Number',  width: 'w-[150px]' },
    { type: 'text', name: 'file_number',     label: 'MRN',     placeholder: 'MRN',     width: 'w-[140px]' },
    { type: 'text', name: 'standard_code',        label: 'Standard Code',        placeholder: 'Standard Code',        width: 'w-[140px]' },
    { type: 'text', name: 'standard_description', label: 'Standard Description', placeholder: 'Standard Description', width: 'w-[170px]' },
    { type: 'text', name: 'agreed_code',          label: 'Agreed Code',          placeholder: 'Agreed Code',          width: 'w-[140px]' },
    { type: 'text', name: 'agreed_desc',          label: 'Agreed Desc',          placeholder: 'Agreed Desc',          width: 'w-[150px]' },
    { type: 'text', name: 'non_standard_code',    label: 'Non Standard Code',    placeholder: 'Non Standard Code',    width: 'w-[160px]' },
    { type: 'text', name: 'non_standard_description', label: 'Non Standard Description', placeholder: 'Non Standard Description', width: 'w-[170px]' },
    {
      type: 'dropdown', name: 'is_nahdi', label: 'Is Nahdi',
      options: [
        { label: t('All'), value: '' },
        { label: t('True'), value: true },
        { label: t('False'), value: false },
      ],
      width: 'w-[140px]',
    },
    ...(!shouldSkipItemCategory ? [{
      type: 'dropdown', name: 'category', label: 'Category',
      options: [
        { label: t('Insurance'),  value: 'insurance'  },
        { label: t('Corporate'),  value: 'corporate'  },
        { label: t('Government'), value: 'government' },
      ],
      placeholder: 'Insurance', width: 'w-[150px]',
    }] : []),
    {
      type: 'dropdown', name: 'format', label: 'Export Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF',   value: 'pdf'   },
      ],
      width: 'w-[130px]',
    },
  ], [payers, departments, policies, filters.payer_id, shouldSkipItemCategory, t, filters.start_date])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between mb-2'>
        <PageHeader
          title={t('Pre Claim Submission Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleExport}
        onReset={handleReset}
        loading={jobLoading}
      />

      {/* <div className='flex gap-2 mb-3'>
        <CustomButton
          label={t('Preview')}
          buttonType='primary'
          icon='pi pi-eye'
          className='h-[35px] w-fit'
          loading={previewLoading}
          disabled={previewLoading || jobLoading}
          onClick={handlePreview}
        />
      </div> */}

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('Pre Claim Submission Report')}
      />

      {(previewData.length > 0 || previewLoading) && (
        <div className='mt-3 overflow-x-auto'>
          <p className='text-sm text-gray-500 mb-1'>
            {t('Showing')} {previewData.length} {t('records')}
          </p>
          <CustomTable
            data={previewData}
            columns={previewColumns(t)}
            loading={previewLoading}
            showGridlines
            scrollable
            scrollHeight='500px'
            tableStyle={{ minWidth: '2800px' }}
          />
        </div>
      )}
    </div>
  )
}

export default PreClaimSubmissionReport
